import client from '@/lib/axios-client'
import type { Notice, NoticeInput, NoticeListOutput } from '@/types/notice'

const NOTICES_URL = '/notices'
const NOTICES_URL_ADMIN = '/notices/admin'

export async function getNotices(params?: {
	isActive?: boolean
	page?: number
	limit?: number
	branchId?: string
}): Promise<NoticeListOutput> {
	const response = await client.get(NOTICES_URL, { params })
	return response.data.data
}

export async function getNoticesAdmin(params?: {
	isActive?: boolean
	page?: number
	limit?: number
	branchId?: string
}): Promise<NoticeListOutput> {
	const response = await client.get(NOTICES_URL_ADMIN, { params })
	return response.data.data
}

export async function getNoticeById(id: string): Promise<Notice> {
	const response = await client.get(`${NOTICES_URL}/${id}`)
	return response.data.data
}

export async function getNoticeByIdAdmin(id: string): Promise<Notice> {
	const response = await client.get(`${NOTICES_URL_ADMIN}/${id}`)
	return response.data.data
}

export async function markNoticeAsRead(id: string): Promise<void> {
	await client.post(`${NOTICES_URL}/${id}/read`)
}

export type UploadProgressHandler = (percent: number) => void

function toNoticeFormData(data: Partial<NoticeInput>): FormData {
	const formData = new FormData()
	const { branchIds, roles, mediaFiles, keepMediaIds, ...fields } = data

	for (const [key, value] of Object.entries(fields)) {
		if (value === undefined) continue
		formData.append(key, value === null ? '' : String(value))
	}
	for (const branchId of branchIds ?? []) {
		formData.append('branchIds', branchId)
	}
	for (const role of roles ?? []) {
		formData.append('roles', role)
	}
	// JSON porque uma lista vazia ("remover todas as mídias") não existe em multipart
	if (keepMediaIds) {
		formData.append('keepMediaIds', JSON.stringify(keepMediaIds))
	}
	for (const file of mediaFiles ?? []) {
		formData.append('media', file)
	}

	return formData
}

function uploadConfig(onProgress?: UploadProgressHandler) {
	return {
		headers: { 'Content-Type': 'multipart/form-data' },
		onUploadProgress: (event: { loaded: number; total?: number }) => {
			if (onProgress && event.total) {
				onProgress(Math.round((event.loaded / event.total) * 100))
			}
		},
	}
}

export async function createNotice(
	data: NoticeInput,
	onProgress?: UploadProgressHandler,
): Promise<Notice> {
	const response = await client.post(
		NOTICES_URL_ADMIN,
		toNoticeFormData(data),
		uploadConfig(onProgress),
	)
	return response.data.data
}

export async function updateNotice(
	id: string,
	data: Partial<NoticeInput>,
	onProgress?: UploadProgressHandler,
): Promise<Notice> {
	const response = await client.put(
		`${NOTICES_URL_ADMIN}/${id}`,
		toNoticeFormData(data),
		uploadConfig(onProgress),
	)
	return response.data.data
}

export async function deleteNotice(id: string): Promise<void> {
	await client.delete(`${NOTICES_URL_ADMIN}/${id}`)
}
